import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { DeviceIcon } from '@/components/DeviceIcon';
import { useStore } from '@/state/store';
import { deviceLabel } from '@/lib/deviceIcons';
import { useI18n } from '@/i18n/I18nContext';
import { Copy } from 'lucide-react';

export const DeviceNode = ({ data, selected }) => {
  const { language, t } = useI18n();
  const vlan = useStore((s) => s.doc.vlans.find((v) => v.id === data.vlanRef));
  const highlightVlanRef = useStore((s) => s.highlightVlanRef);
  const isAlias = !!data.aliasOf;
  const labelPos = useStore((s) => {
    const p = s.doc.pages.find((pp) => pp.id === s.activePageId);
    return p?.labelPos === 'left' ? 'left' : 'below';
  });
  const name = data.label || deviceLabel(data.deviceType, language);
  const typeText = deviceLabel(data.deviceType, language);
  // Show the type as a small line ABOVE the label, but skip it when the label
  // is just the default type name (avoids showing the same text twice).
  const showType = name !== typeText;
  const vlanText = vlan ? 'VLAN' + vlan.vlanId : '';
  const ip = data.ip || '';

  // VLAN highlight: a device matches when its management VLAN OR any of its
  // ports (untagged/tagged) reference the highlighted VLAN. Non-matches dim.
  const matchesHighlight =
    !highlightVlanRef ||
    data.vlanRef === highlightVlanRef ||
    (data.ports || []).some(
      (p) => p.untaggedVlanRef === highlightVlanRef || (Array.isArray(p.taggedVlanRefs) && p.taggedVlanRefs.includes(highlightVlanRef))
    );
  const dimmed = !!highlightVlanRef && !matchesHighlight;
  const emphasized = !!highlightVlanRef && matchesHighlight;

  const labelBlock = (
    <>
      {showType && (
        <div className="truncate text-[9px] font-medium uppercase tracking-wide text-[#7a8591]" data-testid="device-node-type">
          {typeText}
        </div>
      )}
      <div
        className="break-words text-[12px] font-semibold leading-tight text-[#111418]"
        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
      >
        {name}
      </div>
      {vlanText && <div className="truncate font-mono text-[10px] text-[#5b6672]">{vlanText}</div>}
      {ip && <div className="truncate font-mono text-[10px] text-[#5b6672]">{ip}</div>}
    </>
  );

  return (
    <div
      className="relative flex flex-col items-center transition-opacity duration-150"
      style={{ width: 64, opacity: dimmed ? 0.22 : 1 }}
      data-testid="device-node"
      data-highlight={emphasized ? 'true' : 'false'}
    >
      <div
        className={`relative rounded-[8px] bg-white transition-all duration-150 ${
          selected
            ? 'ring-2 ring-[#0B5FFF] shadow-sm'
            : emphasized
            ? 'ring-2 shadow-sm'
            : 'ring-1 ring-[#1b232c]/15 hover:ring-[#2A3138]/50'
        }`}
        style={{ padding: 4, width: 64, height: 64, ...(emphasized && !selected ? { '--tw-ring-color': vlan ? vlan.color : '#0B5FFF', boxShadow: `0 0 0 2px ${vlan ? vlan.color : '#0B5FFF'}` } : {}) }}
      >
        {/* Original handles first so "auto" (undefined handle) keeps its
            defaults (first source = right, first target = left). Each side then
            ALSO gets the complementary type, so any docking side can be used as
            source OR target — otherwise picking e.g. source=left (a target-only
            handle) made React Flow drop the edge from the canvas. */}
        <Handle type="target" position={Position.Left} id="l" style={{ top: 32 }} />
        <Handle type="source" position={Position.Right} id="r" style={{ top: 32 }} />
        <Handle type="target" position={Position.Top} id="t" style={{ left: 32 }} />
        <Handle type="source" position={Position.Bottom} id="b" style={{ left: 32 }} />
        <Handle type="source" position={Position.Left} id="l" style={{ top: 32 }} />
        <Handle type="target" position={Position.Right} id="r" style={{ top: 32 }} />
        <Handle type="source" position={Position.Top} id="t" style={{ left: 32 }} />
        <Handle type="target" position={Position.Bottom} id="b" style={{ left: 32 }} />
        <DeviceIcon type={data.deviceType} size={56} />
        {vlan && (
          <span
            className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-2 border-white"
            style={{ background: vlan.color }}
          />
        )}
        {isAlias && (
          <span
            className="absolute -left-1.5 -top-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-[#6B4FBB] text-white shadow-sm"
            title={t('alias_badge_title')}
            data-testid="device-node-alias-badge"
          >
            <Copy className="h-2.5 w-2.5" strokeWidth={2.5} />
          </span>
        )}
      </div>

      {labelPos === 'left' ? (
        <div
          className="absolute top-1/2 flex w-[190px] -translate-y-1/2 flex-col justify-center text-right leading-tight"
          style={{ right: 72 }}
          data-testid="device-node-label-left"
        >
          {labelBlock}
        </div>
      ) : (
        <div
          className="mt-1 text-center leading-tight"
          style={{ width: 170, marginLeft: -53 }}
          data-testid="device-node-label-below"
        >
          {labelBlock}
        </div>
      )}
    </div>
  );
};
